/**
 * CSV export mutation for 患者リスト(施術師毎) — downloads CSV file.
 */

import { useMutation } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2, resolveServiceType } from '@/constants/serviceType';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants/messages';
import { exportVisitPatientsCsv } from '@/services/main/visitPatient';
import type { ExportVisitPatientCsvPayload } from '@/pages/main/visitPatient/types';

/** SCR-188 item #2: filename branches on service type. */
function resolveFilename(serviceType: string): string {
  return serviceType === SERVICE_TYPE_A2
    ? 'patient_acupuncture_moxibustion_list.csv'
    : 'patient_massager_list.csv';
}

export function useExportVisitPatientCsvMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    ExportVisitPatientCsvPayload
  >({
    mutationFn: payload =>
      exportVisitPatientsCsv(tenant, clinicNumber, payload, resolveServiceType(serviceType)),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: () => {
      toast.error(CSV_ERROR_MESSAGE);
    },
  });

  const triggerExport = async (payload: ExportVisitPatientCsvPayload) => {
    try {
      const blob = await mutateAsync(payload);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resolveFilename(resolveServiceType(serviceType));
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      // onError handles toast
    }
  };

  return { triggerExport, isLoading };
}
